import React, { createContext, useContext, type ReactNode, useReducer, useEffect, useCallback, useMemo, startTransition } from 'react';
import { useTheme } from './ThemeContext';
import { T, UrlParams, Version } from '@ihs-widget/utils/enums';
import { getUrlParam } from '@ihs-widget/utils/functions';
import { useDataOrchestrator } from './DataOrchestrator';

// Function to check if a color is light or dark
export const isColorLight = (color: string): boolean => {
    // Convert hex to RGB
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Calculate luminance using the formula: 0.299*R + 0.587*G + 0.114*B
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // If luminance is greater than 0.5, the color is light
    return luminance > 0.5;
};

export const isColorDark = (color: string): boolean => {
    return !isColorLight(color);
};

// Function to get appropriate text color based on background color
export const getTextColorForBackground = (backgroundColor: string): string => {
    return isColorLight(backgroundColor) ? '#111111' : '#FFFFFF';
};

// Function to get default background color based on version and theme
const getDefaultBackgroundColor = (version: Version, theme: T): string => {
    if (version === Version.V1) {
        return theme === T.DARK ? '#333333' : '#FFFFFF';
    } else if (version === Version.V2) {
        return theme === T.DARK ? '#222222' : '#EEEEEE';
    }
    return '#FFFFFF';
};

const getDefaultBackgroundColorV2 = (): string => {
    // Manual checking of theme and version from URL
    const themeURL = getUrlParam(UrlParams.THEME);
    const versionURL = getUrlParam(UrlParams.VERSION);
    
    const theme = (themeURL && (themeURL === 'light' || themeURL === 'dark')) ? themeURL as T : T.LIGHT;
    const version = (versionURL && Object.values(Version).includes(versionURL as Version)) ? versionURL as Version : Version.V2;
    const isV1 = version === Version.V1;
    
    if (isV1) {
        return theme === T.DARK ? '#222222' : '#EEEEEE';
    } else {
        return theme === T.DARK ? '#333333' : '#FFFFFF';
    }
};

const getDefaultReverseBackgroundColorV2 = (): string => {
    // Manual checking of theme and version from URL
    const themeURL = getUrlParam(UrlParams.THEME);
    const versionURL = getUrlParam(UrlParams.VERSION);
    
    const theme = (themeURL && (themeURL === 'light' || themeURL === 'dark')) ? themeURL as T : T.LIGHT;
    const version = (versionURL && Object.values(Version).includes(versionURL as Version)) ? versionURL as Version : Version.V2;
    const isV1 = version === Version.V1;
    
    if (isV1) {
        return theme === T.DARK ? '#EEEEEE' : '#222222';
    } else {
        return theme === T.DARK ? '#FFFFFF' : '#333333';
    }
};

const getDefaultTextColorV2 = (): string => {
    // Manual checking of theme from URL
    const themeURL = getUrlParam(UrlParams.THEME);
    const theme = (themeURL && (themeURL === 'light' || themeURL === 'dark')) ? themeURL as T : T.LIGHT;
    
    return theme === T.DARK ? '#FFFFFF' : '#000000';
};

export interface ThemeColors {
    homeColor: string;
    awayColor: string;
    backgroundColor: string;
    reverseBackgroundColor: string;
    textColor: string;
    borderColor: string;
    textAdjustedToBGHome: string;
    textAdjustedToBGAway: string;
}

interface ColorState {
    colors: ThemeColors | null;
    loading: boolean;
    error: string | null;
}

type ColorAction = 
    | { type: 'SET_LOADING'; payload: boolean }
    | { type: 'SET_COLORS'; payload: ThemeColors }
    | { type: 'SET_ERROR'; payload: string }
    | { type: 'RESET' };

const defaultColors: ThemeColors = {
    homeColor: '#147B45',
    awayColor: '#333333',
    backgroundColor: getDefaultBackgroundColorV2(),
    reverseBackgroundColor: getDefaultReverseBackgroundColorV2(),
    textColor: getDefaultTextColorV2(),
    borderColor: getDefaultTextColorV2(),
    textAdjustedToBGHome: getTextColorForBackground('#147B45'),
    textAdjustedToBGAway: getTextColorForBackground('#333333')
};

const initialState: ColorState = {
    colors: defaultColors,
    loading: false,
    error: null
};

const colorReducer = (state: ColorState, action: ColorAction): ColorState => {
    switch (action.type) {
        case 'SET_LOADING':
            return { ...state, loading: action.payload };
        case 'SET_COLORS':
            return { ...state, colors: action.payload, loading: false, error: null };
        case 'SET_ERROR':
            return { ...state, error: action.payload, loading: false, colors: defaultColors };
        case 'RESET':
            return initialState;
        default:
            return state;
    }
};

interface ColorContextType {
    colors: ThemeColors | null;
    loading: boolean;
    error: string | null;
    refreshColors: () => Promise<void>;
    updateTheme: (theme: 'light' | 'dark') => void;
    updateDynamicColors: (isDynamicColors: boolean) => void;
    currentTheme: 'light' | 'dark';
    isDynamicColors: boolean;
    version: string;
}

const ColorContext = createContext<ColorContextType | undefined>(undefined);

interface ColorProviderProps {
    children: ReactNode;
}

export const ColorProvider: React.FC<ColorProviderProps> = ({ 
    children
}) => {
    const { theme, isDynamicColors, version } = useTheme();
    const { apiColors } = useDataOrchestrator();
    const themeString = (theme === 'light' || theme === 'dark') ? theme : 'light';
    // Synchronous color loading at the beginning

    const defaultHomeColorOld = (themeString === 'dark' ? '#4CBD83' : '#0A6638');
    const defaultAwayColorOld = (themeString === 'dark' ? '#FFEF6E' : '#C35000');

    const defaultHomeColor = '#147B45';
    const defaultAwayColor = '#333333';
    
    const getInitialColors = (): ThemeColors => {
        // For dark theme we use dark colors, for light theme we use light colors
        if (themeString === 'dark') {
            return {
                homeColor: defaultHomeColor,
                awayColor: defaultAwayColor,
                backgroundColor: getDefaultBackgroundColor(version, theme),
                reverseBackgroundColor: getDefaultBackgroundColor(version, theme === T.DARK ? T.LIGHT : T.DARK),
                textColor: '#FFFFFF',
                borderColor: '#FFFFFF',
                textAdjustedToBGHome: getTextColorForBackground(defaultHomeColor),
                textAdjustedToBGAway: getTextColorForBackground(defaultAwayColor)
            };
        } else {
            return {
                homeColor: defaultHomeColor,
                awayColor: defaultAwayColor,
                backgroundColor: getDefaultBackgroundColor(version, theme),
                reverseBackgroundColor: getDefaultBackgroundColor(version, theme === T.DARK ? T.LIGHT : T.DARK),
                textColor: '#111111',
                borderColor: '#000000',
                textAdjustedToBGHome: getTextColorForBackground(defaultHomeColor),
                textAdjustedToBGAway: getTextColorForBackground(defaultAwayColor)
            };
        }
    };

    const [state, dispatch] = useReducer(colorReducer, { colors: getInitialColors(), loading: false, error: null });

    // Calculate derived colors based on Orchestrator colors and local theme
    const derivedColors: ThemeColors = useMemo(() => {
        const home = apiColors?.homeColor ?? defaultHomeColor;
        const away = apiColors?.awayColor ?? defaultAwayColor;
        return {
            homeColor: home,
            awayColor: away,
            backgroundColor: getDefaultBackgroundColor(version, theme),
            reverseBackgroundColor: getDefaultBackgroundColor(version, theme === T.DARK ? T.LIGHT : T.DARK),
            textColor: themeString === 'dark' ? '#FFFFFF' : '#111111',
            borderColor: themeString === 'dark' ? '#FFFFFF' : '#000000',
            textAdjustedToBGHome: getTextColorForBackground(home),
            textAdjustedToBGAway: getTextColorForBackground(away)
        };
    }, [apiColors, version, theme, themeString]);

    useEffect(() => {
        startTransition(() => {
            dispatch({ type: 'SET_COLORS', payload: derivedColors });
        });
    }, [derivedColors]);

    const refreshColors = useCallback(async () => {
        // Orchestrator manages color loading, so we don't do anything here
    }, []);

    const updateTheme = (newTheme: 'light' | 'dark') => {
        // ThemeContext manages theme, so we don't change theme directly here
    };

    const updateDynamicColors = (value: boolean) => {
        // ThemeContext manages isDynamicColors, so we don't change it directly here
    };

    const value: ColorContextType = {
        colors: state.colors,
        loading: state.loading,
        error: state.error,
        refreshColors,
        updateTheme,
        updateDynamicColors,
        currentTheme: themeString,
        isDynamicColors,
        version
    };

    return (
        <ColorContext.Provider value={value}>
            {children}
        </ColorContext.Provider>
    );
};

export const useColorContext = (): ColorContextType => {
    const context = useContext(ColorContext);
    if (context === undefined) {
        throw new Error('useColorContext must be used within a ColorProvider');
    }
    return context;
}; 
